/**
 * PATH: packages/ui/src/kanban/components/card-detail/CascadeArchiveModal.tsx
 * FILE_ID: UI-KANBAN-CMP-117
 * PURPOSE: Subtree cascade-archive confirm modal (shared with board menu).
 * WHY: Split from the 4,176-line CardDetailDrawer monolith (UAT P5).
 *      Verbatim move - behaviour unchanged; the host re-exports the
 *      public surface so existing imports keep working.
 */

import { type ReactNode, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Archive, Calendar, CheckSquare, ChevronUp, Clock, CornerDownRight, ExternalLink, FileText, Flag,
  Loader2, Mail, Merge, MessageSquare, Network, Paperclip, Pin, PinOff, Plus,
  RotateCcw,
  ShieldAlert, Tag as TagIcon, Trash2, UserPlus, X,
} from 'lucide-react'
import type { KanbanApi, KanbanEmailAdapter, KanbanMember } from '../../api'
import { KanbanCardConflictError } from '../../api'
import { useFocusTrap } from '../../hooks/useFocusTrap'
import { MentionTextarea } from '../MentionTextarea'
import { PeoplePicker } from '../PeoplePicker'
import type {
  AuditLogEntry,
  Card, CardChecklistItemT, CardChecklistT, CardCommentT,
  CardStatus, Lane, Tag,
} from '../../types'
import { KanbanAvatar } from '../KanbanAvatar'
import { EmailLinkTickCross } from '../EmailLinkTickCross'
import { ChecklistItemContextMenu } from '../ChecklistItemContextMenu'
import { SpeechMicButton } from '../SpeechMicButton'
import type { TrelloCardDetail } from '../../types'

export function CascadeArchiveModal({
  root,
  api,
  onClose,
  onArchived,
}: {
  root: Card
  api: KanbanApi
  onClose: () => void
  onArchived: (count: number) => void
}) {
  const q = useQuery({
    queryKey: ['kanban', 'card', root.id, 'subtree-preview'],
    queryFn: () => api.fetchCardSubtree!(root.id),
    enabled: !!api.fetchCardSubtree,
  })

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef, {
    onEscape: () => {
      if (!busy) onClose()
    },
  })

  async function confirm() {
    if (!api.archiveCardSubtree || busy) return
    setBusy(true)
    setError(null)
    try {
      const res = await api.archiveCardSubtree(root.id)
      onArchived(res.archived)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Archive failed.')
      setBusy(false)
    }
  }

  const subtree = q.data ?? []
  const descendants = subtree.filter((c) => c.id !== root.id)

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Confirm cascade-archive of ${root.title || ''}`}
      data-testid="cascade-archive-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        // Click-away closes; the buttons stop-propagate so a confirm
        // click doesn't double-fire.
        if (e.target === e.currentTarget && !busy) onClose()
      }}
    >
      <div
        className="w-full max-w-md rounded-lg border border-neutral-200 bg-white p-4 shadow-2xl dark:border-neutral-800 dark:bg-neutral-950"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-3 flex items-start gap-2">
          <Archive className="mt-0.5 h-4 w-4 text-red-600 dark:text-red-400" />
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              Archive with sub-cards?
            </h2>
            <p className="mt-0.5 text-[11.5px] leading-snug text-neutral-600 dark:text-neutral-300">
              You're about to archive <span className="font-medium">{root.title || '(untitled)'}</span>{' '}
              and every sub-card under it. The tree shape is preserved,
              so a restore would bring everything back in place.
            </p>
          </div>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-900 dark:hover:text-neutral-100"
            aria-label="Close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        <div className="max-h-[40vh] overflow-y-auto rounded border border-neutral-200/70 bg-neutral-50/60 p-2 dark:border-neutral-800/80 dark:bg-neutral-900/40">
          {q.isLoading && (
            <p className="text-[11px] text-neutral-500">Loading subtree…</p>
          )}
          {q.isError && (
            <p className="text-[11px] text-red-600">Could not load subtree.</p>
          )}
          {!q.isLoading && !q.isError && (
            <>
              <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-neutral-500">
                {descendants.length === 0
                  ? 'No descendants — root only'
                  : `${descendants.length} descendant${descendants.length === 1 ? '' : 's'}`}
              </p>
              <ul className="space-y-0.5">
                <li className="flex items-center gap-1.5 text-[11.5px] font-medium text-neutral-800 dark:text-neutral-100">
                  <Archive className="h-3 w-3 shrink-0 text-red-500" />
                  <span className="truncate">{root.title || '(untitled)'}</span>
                </li>
                {descendants.slice(0, 50).map((c) => (
                  <li
                    key={c.id}
                    className="flex items-center gap-1.5 text-[11px] text-neutral-700 dark:text-neutral-300"
                    style={{
                      paddingLeft: `${Math.min(
                        (c.tree_depth ?? 1) - (root.tree_depth ?? 0), 5,
                      ) * 12 + 4}px`,
                    }}
                  >
                    <CornerDownRight className="h-3 w-3 shrink-0 text-neutral-400" />
                    <span className="truncate">{c.title || '(untitled)'}</span>
                  </li>
                ))}
                {descendants.length > 50 && (
                  <li className="pl-4 text-[10.5px] italic text-neutral-500">
                    …and {descendants.length - 50} more
                  </li>
                )}
              </ul>
            </>
          )}
        </div>

        {error && (
          <p className="mt-2 text-[11px] text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}

        <footer className="mt-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded px-3 py-1.5 text-xs text-neutral-600 hover:bg-neutral-100 disabled:opacity-50 dark:text-neutral-300 dark:hover:bg-neutral-900"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={busy || q.isLoading || q.isError}
            className="inline-flex items-center gap-1.5 rounded bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            data-testid="cascade-archive-confirm"
          >
            {busy ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Archive className="h-3 w-3" />
            )}
            Archive {1 + descendants.length} card
            {descendants.length === 0 ? '' : 's'}
          </button>
        </footer>
      </div>
    </div>
  )
}

/**
 * qb!3096 (2026-05-26) — thin wrapper that fetches the Trello card
 * detail's `email_link` block and renders the tick/cross strip when
 * the BE signals a confirmable / rejectable state. Lives inside
 * CardDetailDrawer.tsx so it can share the existing scoped hooks
 * (useQueryClient, useQuery) without re-exporting them from the
 * kanban surface.
 *
 * Defensive: any failure (no external_id, tenant didn't wire the
 * fetcher, pre-3096 BE, network error) renders nothing — the
 * downstream <EmailThreadView> or <EmailFromCard> still works.
 *
 * Cache-shared with <EmailFromCard> + <TrelloCardDetailSection>
 * via the ['trello-card-detail', external_id] key, so we never
 * trigger a second round-trip.
 */
